import { graphql, useMutation } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { ContextId, ExtensionId } from '@atlassian/forge-ui-types/extension';

import { useTracingContext } from '../../../error-reporting';
import { createAtlAttributionHeader } from '../../../utils';
import { type useFetchAuthToken_forge_ui_userAuthTokenMutation as userAuthTokenMutation } from './__generated__/useFetchAuthToken_forge_ui_userAuthTokenMutation.graphql';

export interface AuthToken {
	expiry: number; // expiry in ms
	token: string;
}

export const MILLIS_IN_SECOND = 1000;

type FetchAuthToken = (extensionId: ExtensionId, contextIds: ContextId[]) => Promise<AuthToken>;

export const useFetchAuthToken = ({
	renderThreeLOPrompt,
}: {
	renderThreeLOPrompt: (retry: () => Promise<void>) => Promise<void>;
}): FetchAuthToken => {
	const tracing = useTracingContext();

	const [commitUserAuthTokenForExtensionMutation] = useMutation<userAuthTokenMutation>(graphql`
		mutation useFetchAuthToken_forge_ui_userAuthTokenMutation(
			$input: UserAuthTokenForExtensionInput!
		) {
			userAuthTokenForExtension(input: $input) {
				success
				errors {
					message
				}
				authToken {
					token
					ttl
				}
			}
		}
	`);

	const fetchAuthToken = async (
		extensionId: ExtensionId,
		contextIds: ContextId[],
	): Promise<AuthToken> => {
		tracing?.recordGqlCall('userAuthTokenForExtension - start');

		return new Promise((resolve, reject) => {
			commitUserAuthTokenForExtensionMutation({
				variables: {
					input: {
						extensionId,
						contextIds,
					},
				},
				// @ts-ignore
				cacheConfig: {
					metadata: {
						headers: {
							'atl-attribution': createAtlAttributionHeader([...contextIds].sort().join()),
						},
					},
				},
				onError: (error) => {
					reject(new Error(`Network error: ${error.message}`));
				},
				onCompleted: (data, topLevelErrors) => {
					if (data?.userAuthTokenForExtension) {
						const { success, authToken, errors } = data.userAuthTokenForExtension;

						tracing?.recordGqlCall('userAuthTokenForExtension - end');

						if (
							!success &&
							errors &&
							errors[0].message?.includes(
								'No Atlassian OAuth token found for this user and extension',
							)
						) {
							renderThreeLOPrompt(async () => {
								const token = await fetchAuthToken(extensionId, contextIds);
								resolve(token);
							});
							return;
						}

						if (success && authToken) {
							const { token, ttl } = authToken;
							resolve({ token, expiry: Date.now() + ttl * MILLIS_IN_SECOND });
							return;
						}
						reject(new Error('An unexpected error occurred when fetching an auth token'));
						return;
					}

					// A null payload (e.g. a top-level access error) used to leave this promise pending forever.
					if (
						fg('forge-ui-request-product-context-ari') ||
						fg('forge-ui-reject-null-auth-token-payload')
					) {
						tracing?.recordGqlCall('userAuthTokenForExtension - end');
						const message = topLevelErrors?.map((e) => e.message).join('; ');
						reject(
							new Error(
								message
									? `Failed to fetch an auth token: ${message}`
									: 'An unexpected error occurred when fetching an auth token',
							),
						);
					}
				},
			});
		});
	};

	return fetchAuthToken;
};
